import { useEffect, useRef, useState, useCallback } from 'react'
import {
  VerifierEvent,
  VerificationPhase,
  VerificationRequestEvent,
  CryptoEvent,
} from 'matrix-js-sdk/lib/crypto-api'
import { getClient } from '@/lib/matrix/client'
import type {
  VerificationRequest,
  Verifier,
  ShowSasCallbacks,
  EmojiMapping,
} from 'matrix-js-sdk/lib/crypto-api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import {
  listOwnSessions,
  requestSelfVerification,
  getCrossSigningState,
  unlockCrossSigning,
  setUpCrossSigning,
  type OwnSession,
  type CrossSigningState,
} from '@/lib/matrix/verification'

const SAS_METHOD = 'm.sas.v1'

type Stage = 'list' | 'incoming' | 'waiting' | 'sas' | 'done' | 'cancelled'

interface DeviceVerificationDialogProps {
  onClose: () => void
  incomingRequest?: VerificationRequest | null
}

export function DeviceVerificationDialog({ onClose, incomingRequest }: DeviceVerificationDialogProps) {
  const [sessions, setSessions] = useState<OwnSession[]>([])
  const [loading, setLoading] = useState(true)
  const [stage, setStage] = useState<Stage>('list')
  const [emoji, setEmoji] = useState<EmojiMapping[] | null>(null)

  const reqRef = useRef<VerificationRequest | null>(null)
  const sasRef = useRef<ShowSasCallbacks | null>(null)
  const startedRef = useRef(false)
  const attachedRef = useRef(false)

  const refresh = useCallback(() => {
    listOwnSessions()
      .then(setSessions)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Crypto loads asynchronously; re-check sessions when keys/devices change so
  // the list reflects reality without needing to reopen the dialog.
  useEffect(() => {
    const client = getClient()
    if (!client) return
    const onChange = () => refresh()
    client.on(CryptoEvent.KeysChanged, onChange)
    client.on(CryptoEvent.DevicesUpdated, onChange)
    return () => {
      client.off(CryptoEvent.KeysChanged, onChange)
      client.off(CryptoEvent.DevicesUpdated, onChange)
    }
  }, [refresh])

  const attach = useCallback((verifier: Verifier) => {
    if (attachedRef.current) return
    attachedRef.current = true
    verifier.on(VerifierEvent.ShowSas, (cb: ShowSasCallbacks) => {
      sasRef.current = cb
      setEmoji(cb.sas.emoji ?? null)
      setStage('sas')
    })
    verifier
      .verify()
      .then(() => {
        setStage('done')
        refresh()
      })
      .catch(() => setStage('cancelled'))
  }, [refresh])

  const drive = useCallback((request: VerificationRequest, weInitiated: boolean) => {
    reqRef.current = request
    startedRef.current = false
    attachedRef.current = false
    setStage('waiting')

    const onChange = () => {
      const phase = request.phase
      if (phase === VerificationPhase.Cancelled) {
        setStage('cancelled')
        return
      }
      if (phase === VerificationPhase.Done) {
        setStage('done')
        refresh()
        return
      }
      // Initiator kicks off SAS once both sides are ready; both sides then get
      // a verifier (and the emoji) to compare.
      if (weInitiated && phase === VerificationPhase.Ready && !startedRef.current) {
        startedRef.current = true
        request.startVerification(SAS_METHOD).then(attach).catch(() => setStage('cancelled'))
      }
      if (request.verifier && !attachedRef.current) attach(request.verifier)
    }

    request.on(VerificationRequestEvent.Change, onChange)
    onChange()
  }, [attach, refresh])

  // A verification request initiated by another session, handed in from outside.
  useEffect(() => {
    if (!incomingRequest) return
    reqRef.current = incomingRequest
    setStage('incoming')
  }, [incomingRequest])

  const cancelActive = useCallback(() => {
    const req = reqRef.current
    if (req && req.phase !== VerificationPhase.Done && req.phase !== VerificationPhase.Cancelled) {
      req.cancel().catch(() => {})
    }
  }, [])

  useEffect(() => () => cancelActive(), [cancelActive])

  const acceptIncoming = async () => {
    const req = reqRef.current
    if (!req) return
    try {
      await req.accept()
      drive(req, false)
    } catch {
      setStage('cancelled')
    }
  }

  const startVerify = async (deviceId: string) => {
    try {
      drive(await requestSelfVerification(deviceId), true)
    } catch {
      setStage('cancelled')
    }
  }

  const confirmSas = () => {
    sasRef.current?.confirm()
    setStage('waiting')
  }
  const mismatchSas = () => {
    sasRef.current?.mismatch()
    setStage('cancelled')
  }

  const backToList = () => {
    reqRef.current = null
    sasRef.current = null
    setEmoji(null)
    setStage('list')
  }

  const close = () => {
    cancelActive()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={close}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))]">
            {stage === 'sas' || stage === 'waiting' || stage === 'incoming' ? 'Verify session' : 'Sessions & security'}
          </h2>
          <button onClick={close} className="p-1 text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {stage === 'list' && (
          <div className="space-y-4">
            <CrossSigningSection onChanged={refresh} />
            {loading ? (
              <div className="flex justify-center py-6"><Spinner size={20} /></div>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {sessions.map((s) => (
                  <div key={s.deviceId} className="flex items-center gap-3 px-3 py-2 rounded-md border border-[rgb(var(--np-border)/0.5)]">
                    <VerifiedIcon verified={s.verified} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[rgb(var(--np-tx-primary))] truncate">
                        {s.displayName}
                        {s.isCurrent && <span className="ml-1.5 text-[10px] text-[rgb(var(--np-accent))]">(this device)</span>}
                      </div>
                      <div className="text-[11px] font-data text-[rgb(var(--np-tx-muted))] truncate">{s.deviceId}</div>
                    </div>
                    {!s.verified && !s.isCurrent && (
                      <button
                        onClick={() => startVerify(s.deviceId)}
                        className="text-xs font-medium text-[rgb(var(--np-accent))] hover:underline shrink-0"
                      >
                        Verify
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {stage === 'incoming' && (
          <div className="space-y-4">
            <p className="text-sm text-[rgb(var(--np-tx-secondary))]">
              Another session wants to verify with this one. Accept to compare emoji.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { cancelActive(); backToList() }}>Decline</Button>
              <Button onClick={acceptIncoming}>Accept</Button>
            </div>
          </div>
        )}

        {stage === 'waiting' && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Spinner size={24} />
            <p className="text-sm text-[rgb(var(--np-tx-muted))]">Waiting for the other session…</p>
          </div>
        )}

        {stage === 'sas' && (
          <div className="space-y-4">
            <p className="text-sm text-[rgb(var(--np-tx-secondary))]">
              Confirm the same emoji appear in the same order on both sessions.
            </p>
            <div className="grid grid-cols-4 gap-3">
              {(emoji ?? []).map(([char, name], i) => (
                <div key={i} className="flex flex-col items-center gap-1 py-2 rounded-md bg-[rgb(var(--np-raised))]">
                  <span className="text-2xl leading-none">{char}</span>
                  <span className="text-[10px] text-[rgb(var(--np-tx-muted))] capitalize">{name}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={mismatchSas}>They don't match</Button>
              <Button onClick={confirmSas}>They match</Button>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="text-[rgb(var(--np-status-ok,34_197_94))]">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <p className="text-sm text-[rgb(var(--np-tx-primary))]">Session verified</p>
            <Button variant="secondary" onClick={backToList}>Back</Button>
          </div>
        )}

        {stage === 'cancelled' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-sm text-[rgb(var(--np-tx-muted))]">Verification cancelled</p>
            <Button variant="secondary" onClick={backToList}>Back</Button>
          </div>
        )}
      </div>
    </div>
  )
}

const CROSS_SIGNING_COPY: Record<CrossSigningState, string> = {
  ready: 'Cross-signing is set up. Verifications here are trusted across your account.',
  locked: 'Cross-signing exists on your account but is not loaded on this device. Unlock it with your recovery key, or verify this session from one that’s already verified.',
  not_set_up: 'Cross-signing has never been set up for this account. Set it up so verifications persist and propagate.',
}

function CrossSigningSection({ onChanged }: { onChanged: () => void }) {
  const [state, setState] = useState<CrossSigningState | null>(null)
  const [form, setForm] = useState<'none' | 'unlock' | 'setup'>('none')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    getCrossSigningState().then(setState).catch(() => setState(null))
  }, [])

  useEffect(() => {
    load()
    const client = getClient()
    if (!client) return
    const onChange = () => load()
    client.on(CryptoEvent.KeysChanged, onChange)
    return () => {
      client.off(CryptoEvent.KeysChanged, onChange)
    }
  }, [load])

  if (state === null) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!secret.trim()) return
    setBusy(true)
    setError(null)
    try {
      if (form === 'unlock') await unlockCrossSigning(secret.trim())
      else await setUpCrossSigning(secret)
      setSecret('')
      setForm('none')
      load()
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed. Check the value and try again.')
    } finally {
      setBusy(false)
    }
  }

  const tone =
    state === 'ready'
      ? 'bg-[rgb(var(--np-accent)/0.08)] text-[rgb(var(--np-tx-secondary))]'
      : 'bg-[rgb(var(--np-status-warn)/0.1)] text-[rgb(var(--np-status-warn))]'

  return (
    <div className={`text-xs px-3 py-2.5 rounded-md space-y-2 ${tone}`}>
      <p>{CROSS_SIGNING_COPY[state]}</p>

      {state !== 'ready' && form === 'none' && (
        <button
          type="button"
          onClick={() => { setError(null); setForm(state === 'locked' ? 'unlock' : 'setup') }}
          className="font-medium underline"
        >
          {state === 'locked' ? 'Unlock with recovery key' : 'Set up cross-signing'}
        </button>
      )}

      {form !== 'none' && (
        <form onSubmit={submit} className="space-y-2">
          <Input
            autoFocus
            type={form === 'setup' ? 'password' : 'text'}
            mono={form === 'unlock'}
            placeholder={form === 'unlock' ? 'EsTc aBcD eFgH …' : 'Your account password'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            disabled={busy}
          />
          {error && <p className="text-[rgb(var(--np-status-critical))]">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => { setForm('none'); setSecret(''); setError(null) }} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !secret.trim()}>
              {busy ? <Spinner size={14} /> : form === 'unlock' ? 'Unlock' : 'Set up'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

function VerifiedIcon({ verified }: { verified: boolean }) {
  return verified ? (
    <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ) : (
    <svg className="shrink-0 text-[rgb(var(--np-tx-muted))]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )
}
